import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, tap } from 'rxjs';
import {
  OPE_AUTH_CSRF,
  OPE_AUTH_SEND_OTP_EMAIL,
  OPE_AUTH_SEND_OTP_PHONE,
  OPE_AUTH_VERIFY_OTP,
} from '../../constants/endpoints';
import { EmailOtpRequest, PhoneOtpRequest } from '../../dto/otp-request';
import { ApiResponse } from '../../dto/api-response';
import { CsrfData } from '../../dto/csrf-response';
import { VerifyOtpRequest } from '../../dto/verify-otp-request';
import { VerifyOtpResponse } from '../../dto/verify-otp-response';
import { Borrower } from '../../models/borrower';
import { Application, OtpMethod } from '../../models/application';
import { StoreService } from '../store/store.service';
import { handleGenericHttpError } from '../../utils/http-error.util';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  // Injected dependencies
  private http = inject(HttpClient);
  private store = inject(StoreService);

  /**
   * Fetch CSRF token from the server. The server sets the XSRF-TOKEN cookie via Set-Cookie.
   * Used by the app initializer on startup; the interceptor uses the cookie for state-changing requests.
   */
  public getCsrf(): Observable<CsrfData> {
    return this.http
      .get<
        ApiResponse<CsrfData>
      >(OPE_AUTH_CSRF, { responseType: 'json', withCredentials: true })
      .pipe(map((r) => r.data));
  }

  /**
   * Send an otp request to a provided email
   * @param email Email to send otp request to
   * @return Observable on completion
   */
  public sendEmailOtp(email: string): Observable<void> {
    if (!email || email.trim() === '') throw new Error('email cannot be empty');

    // Perform http request
    const body: EmailOtpRequest = { email };
    return this.http
      .post<ApiResponse<{ message: string }>>(OPE_AUTH_SEND_OTP_EMAIL, body)
      .pipe(
        tap((r) => {
          // Update only borrower's email
          const borrower: Borrower = {
            email: email,
            phone: '',
            availableBalance: -1,
            firstName: '',
            lastName: '',
          };
          this.store.setBorrower(borrower);
        }),
        map((r) => {
          console.debug(
            '[AUTH-SERVICE]: otp request completed, message: ',
            r.data.message,
          );
          return;
        }),
        catchError(handleGenericHttpError),
      );
  }

  /**
   * Send an otp request to a provided phone
   * @param phone Phone number to send otp to
   * @return Observable on completion
   */
  public sendPhoneOtp(phone: string): Observable<void> {
    if (!phone || phone.trim() === '') throw new Error('phone cannot be empty');

    // Perform http request
    const body: PhoneOtpRequest = { phone };
    return this.http
      .post<ApiResponse<{ message: string }>>(OPE_AUTH_SEND_OTP_PHONE, body)
      .pipe(
        tap((r) => {
          // Update only borrower's email
          const borrower: Borrower = {
            email: '',
            phone: phone,
            availableBalance: -1,
            firstName: '',
            lastName: '',
          };
          this.store.setBorrower(borrower);
        }),
        map((r) => {
          console.debug(
            '[AUTH-SERVICE]: otp request completed, message: ',
            r.data.message,
          );
          return;
        }),
        catchError(handleGenericHttpError),
      );
  }

  /**
   * Verify OTP and submit application to Oscilar (application flow).
   * Backend returns application status (APPROVED/DENIED) and sets JWT in cookie.
   */
  public verifyOtpWithApplication(
    dest: string,
    method: OtpMethod,
    otp: string,
    applicationGuid: string,
  ): Observable<VerifyOtpResponse> {
    if (!dest || dest.trim() === '') throw new Error('dest cannot be empty');
    if (!otp || otp.trim() === '') throw new Error('otp cannot be empty');
    if (!applicationGuid || applicationGuid.trim() === '')
      throw new Error('applicationGuid cannot be empty');

    const body: VerifyOtpRequest = {
      dest,
      otp,
      applicationGuid,
      otp_method: method,
    };
    return this.http
      .post<ApiResponse<VerifyOtpResponse>>(OPE_AUTH_VERIFY_OTP, body)
      .pipe(
        tap((r) => {
          const data = r.data;
          const current = this.store.snapshot.application;
          if (current && current.applicationGuid === data.applicationGuid) {
            const updated = new Application(
              data.applicationGuid,
              data.creditLimit ?? '',
              data.status,
              data.otpMethod ?? current.otpMethod,
              current.firstName,
              current.lastName,
              current.email,
              current.phone,
              current.street,
              current.apt,
              current.city,
              current.state,
              current.zip,
              current.dateOfBirth,
              current.ssn,
              current.income,
              current.dealerId,
            );
            this.store.setApplication(updated);
          }
        }),
        map((r) => r.data),
        catchError(handleGenericHttpError),
      );
  }
}
